import { Injectable, inject } from '@angular/core';
import {
    destroyDetachedRouteHandle,
    type ActivatedRouteSnapshot,
    type DetachedRouteHandle,
    type Route,
    type RouteReuseStrategy
} from '@angular/router';
import { RoutingService } from './routing/routing.service';

@Injectable({ providedIn: 'root' })
export class ReuseTaggedRoutesStrategy implements RouteReuseStrategy {
    private _routingService = inject(RoutingService);

    private handles = new Map<Route, DetachedRouteHandle>();

    constructor() {
        this._routingService.listenToNavigationReset$().subscribe(() => {
            this.deactivateAllHandles();
        });
    }

    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        if (!route.routeConfig || route.routeConfig.loadChildren) {
            return false;
        }
        return !!(route.routeConfig.data && route.routeConfig.data.reuse);
    }

    store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
        if (!route.routeConfig) {
            return;
        }
        this.handles.set(route.routeConfig, handle);
    }

    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        if (!route.routeConfig) {
            return false;
        }
        return this.handles.has(route.routeConfig);
    }

    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        const handle = route.routeConfig ? this.handles.get(route.routeConfig) : null;
        if (!handle || route.routeConfig?.loadChildren) {
            return null;
        }
        return handle;
    }

    shouldReuseRoute(future: ActivatedRouteSnapshot, current: ActivatedRouteSnapshot): boolean {
        // default strategy, prevents reload when navigating to the same page
        return future.routeConfig === current.routeConfig;
    }

    shouldDestroyInjector(route: Route): boolean {
        return !this.handles.has(route);
    }

    retrieveStoredRouteHandles(): DetachedRouteHandle[] {
        return Array.from(this.handles.values());
    }

    private deactivateAllHandles() {
        this.handles.forEach(handle => {
            destroyDetachedRouteHandle(handle);
        });
        this.handles = new Map();
    }
}
